import type { MaybeRef } from 'vue';
import { DOCUMENT } from '@pkg/config/sanity/schemas';
import { LISTING_TYPES, type ListingPost } from '@pkg/types/listing';
import {
  absolutePageUrlFromPath,
  injectJsonLdScript,
  useJsonLdShared,
} from './useJsonLdShared';

/** Document types that emit CollectionPage + ItemList on full hub listings. */
const COLLECTION_LISTING_DOCS = new Set<string>([
  DOCUMENT.ABOUT_FOOD_WASTE_BLOG,
  DOCUMENT.PRESS_RELEASES,
  // Closest public hub to “impact report” in this codebase (downloadable resources).
  DOCUMENT.ABOUT_FOOD_WASTE_RESOURCES,
]);

const COLLECTION_NAMES: Record<string, string> = {
  [DOCUMENT.ABOUT_FOOD_WASTE_BLOG]: 'Blog',
  [DOCUMENT.PRESS_RELEASES]: 'Press',
  [DOCUMENT.ABOUT_FOOD_WASTE_RESOURCES]: 'Resources',
};

/**
 * Documents whose detail pages already emit `#article` JSON-LD
 * (prefer ItemList cross-ref over duplicating url/name).
 */
const ARTICLE_FRAGMENT_DOCS = new Set<string>([
  DOCUMENT.PRESS_RELEASES, // NewsArticle
  DOCUMENT.ABOUT_FOOD_WASTE_BLOG, // BlogPosting
]);

export interface ListingCollectionJsonLdInput {
  listingDocument?: string;
  listingType?: string;
  posts?: ListingPost[] | null;
  /** Optional CMS page SEO for CollectionPage name/description */
  pageTitle?: string;
  pageDescription?: string;
}

/**
 * CollectionPage + ItemList for blog / press / resources hub listings.
 * Only emits for full listings (LISTING_TYPES.ALL), not LATEST teasers or author category lists.
 * BreadcrumbList stays in the separate cms-breadcrumb-jsonld script.
 */
export function useListingCollectionJsonLd(
  input: MaybeRef<ListingCollectionJsonLdInput | null | undefined>,
) {
  const route = useRoute();
  const localePath = useLocalePath();
  const { baseUrl } = useJsonLdShared();

  const jsonLd = computed(() => {
    const value = unref(input);
    const listingDocument = value?.listingDocument;
    const listingType = value?.listingType;
    const posts = value?.posts;

    if (!listingDocument || !COLLECTION_LISTING_DOCS.has(listingDocument)) {
      return null;
    }
    // Full hub listing only (Sanity value `listing`). Skip latest teasers / category author lists.
    if (listingType && listingType !== LISTING_TYPES.ALL) {
      return null;
    }
    if (!Array.isArray(posts) || posts.length === 0) {
      return null;
    }

    const pageUrl = absolutePageUrlFromPath(baseUrl.value, route.path);
    if (!pageUrl) {
      return null;
    }

    const name = (
      value?.pageTitle ||
      COLLECTION_NAMES[listingDocument] ||
      ''
    ).trim();
    if (!name) {
      return null;
    }

    const description = (value?.pageDescription || '').trim();
    const webpageId = `${pageUrl}#webpage`;
    const itemListId = `${pageUrl}#itemlist`;
    const useArticleFragment = ARTICLE_FRAGMENT_DOCS.has(listingDocument);

    const itemListElement = posts.flatMap((post, index) => {
      const listItem = buildListItem(post, index + 1, {
        baseUrl: baseUrl.value,
        localePath,
        useArticleFragment,
      });
      return listItem ? [listItem] : [];
    });

    if (!itemListElement.length) {
      return null;
    }

    return {
      '@context': 'https://schema.org',
      '@graph': [
        {
          '@type': 'CollectionPage',
          '@id': webpageId,
          url: pageUrl,
          name,
          ...(description && { description }),
          mainEntity: { '@id': itemListId },
        },
        {
          '@type': 'ItemList',
          '@id': itemListId,
          numberOfItems: itemListElement.length,
          itemListElement,
        },
      ],
    };
  });

  injectJsonLdScript('listing-collection-jsonld', jsonLd);
}

function buildListItem(
  post: ListingPost,
  position: number,
  options: {
    baseUrl: string;
    localePath: (path: string) => string;
    useArticleFragment: boolean;
  },
) {
  const title = (post.title || '').trim();
  const absoluteUrl = resolveListingItemAbsoluteUrl(post, options.baseUrl, options.localePath);
  if (!absoluteUrl) {
    return null;
  }

  // Prefer cross-ref to the item's own NewsArticle/BlogPosting node when it exists.
  if (options.useArticleFragment && isInternalArticleUrl(absoluteUrl, options.baseUrl)) {
    return {
      '@type': 'ListItem' as const,
      position,
      item: { '@id': `${absoluteUrl}#article` },
      ...(title && { name: title }),
    };
  }

  return {
    '@type': 'ListItem' as const,
    position,
    url: absoluteUrl,
    ...(title && { name: title }),
  };
}

/** Same link rules as Listing.vue `handlePostLink`, as absolute URLs. */
function resolveListingItemAbsoluteUrl(
  post: ListingPost,
  siteBaseUrl: string,
  localePath: (path: string) => string,
): string | null {
  if (post?.file?.url) {
    return String(post.file.url);
  }
  if (post?.url) {
    return toAbsoluteUrl(String(post.url), siteBaseUrl);
  }
  if (typeof post.slug === 'string' && post.slug.trim()) {
    const path = post.slug.startsWith('/') ? post.slug : `/${post.slug}`;
    return `${siteBaseUrl}${localePath(path)}`;
  }
  return null;
}

function toAbsoluteUrl(url: string, siteBaseUrl: string): string {
  if (/^https?:\/\//i.test(url)) {
    return url;
  }
  if (url.startsWith('/')) {
    return `${siteBaseUrl}${url}`;
  }
  return url;
}

function isInternalArticleUrl(absoluteUrl: string, siteBaseUrl: string): boolean {
  try {
    return new URL(absoluteUrl).origin === new URL(siteBaseUrl).origin;
  } catch {
    return absoluteUrl.startsWith(siteBaseUrl);
  }
}
